import Table, { Action, Column } from "@components/Table";
import { getBoat } from "@state/DataStore";
import { numberToCurrency } from "@utils/string";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Error from "@components/Error";
import { useTranslation } from "react-i18next";
import { ReservationType } from "src/types/types";
import useLoading from "src/services/LoadingService";
import useDraftReservations from "./use-draft-reservations";

const TablePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columns: Column<ReservationType>[] = useMemo(
    () => [
      { name: "id", title: "#", align: "right" },
      {
        name: "boatId",
        title: t("Boat"),
        formatter: (v) => getBoat(v as number)?.name ?? "",
      },
      {
        name: "date",
        title: t("Date"),
        align: "right",
        formatter: (v) => new Date(v as string).toLocaleDateString(),
      },
      { name: "name", title: t("Name") },
      { name: "email", title: t("Email") },
      { name: "phone", title: t("Phone") },
      {
        name: "price",
        title: t("Price"),
        align: "right",
        formatter: (v) => numberToCurrency(v as number),
      },
    ],
    [t]
  );

  const {
    fetchReservations,
    loading: reservationsLoading,
    error: reservetionsError,
    reservations,
  } = useDraftReservations();

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const actions = useMemo<Action[]>(
    () => [
      {
        title: t<string>("Detail"),
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
        ),
        onClick: (id) => {
          navigate(`/detail/${id}`);
        },
      },
    ],
    [navigate, t]
  );

  useLoading(reservationsLoading);

  if (reservetionsError)
    return <Error onAgain={fetchReservations}>{reservetionsError}</Error>;

  return (
    <Table
      idProperty="id"
      columns={columns}
      data={reservations.filter(({ status }) => status === "draft")}
      actions={actions}
    />
  );
};

export default TablePage;
